// @ts-strict-ignore
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { CollectionListNavigationParams, ScreenNavigationParams, TabConfig } from 'mytours-read-common'

import { CollectionItemsNavigationParams } from '../collection/types'

import { ionHistory } from './'
import {
	getListUrl,
	getTourUrl,
	getStoryUrl,
	getWebUrl,
	getPanoramaUrl,
	getCollectionItemsUrl,
	formatLanguageUrl,
	getTabIndex,
	getListItemUrl,
	SCANNER_PAGE_SLUG,
	SETTINGS_PAGE_SLUG,
	MORE_TAB_SLUG,
	CollectionItemsSlug,
	getMenuUrl,
	PROFILE_PAGE_SLUG,
	PURCHASED_CONTENT_PAGE_SLUG,
} from './routes'
import { HistoryParamsType } from './types'

const navigationStack = {
	tabs: {} as Record<string, string>,
	_activeTab: null,
	_showTabBar: null,
	setActiveTabIndex: (tabIndex?: string | number): void => {
		if (tabIndex) {
			navigationStack._activeTab = `${tabIndex}`
		}
	},
	getActiveTabIndex: (): string => {
		if (!navigationStack._activeTab && navigationStack._showTabBar) {
			return '1'
		} else if (
			!navigationStack._showTabBar ||
			[SCANNER_PAGE_SLUG, SETTINGS_PAGE_SLUG, MORE_TAB_SLUG, PROFILE_PAGE_SLUG, PURCHASED_CONTENT_PAGE_SLUG].includes(
				navigationStack._activeTab,
			)
		) {
			return ''
		} else {
			return navigationStack._activeTab
		}
	},
	setTabs: (tabs: TabConfig[], currentPath?: string | undefined): void => {
		navigationStack._showTabBar = tabs && tabs.length > 1
		tabs.forEach((_tab: TabConfig, index: number) => {
			navigationStack.tabs[`${index + 1}`] = formatLanguageUrl(`/${getTabIndex(index)}`)
		})

		if (currentPath) {
			const activeTab = Object.keys(navigationStack.tabs).find(key => currentPath.startsWith(navigationStack.tabs[key]))

			if (activeTab !== null && activeTab !== undefined && navigationStack._activeTab !== activeTab) {
				navigationStack._activeTab = activeTab
			}
		}
	},
}

// Is referenced in mytours-read-common/src/collectionlist/containers/collectionlist.ts but the actual component does not use it
const navigateToCollectionList = (params: CollectionListNavigationParams) => {
	const url = getListUrl(params.collectionId)
	ionHistory.push(url, {
		...params,
		prevPagePath: ionHistory.location.pathname,
	})
}

// Is referenced in mytours-read-common/src/collectionlist/containers/collectionlist.ts but the actual component does not use it
const navigateToMenu = (params: CollectionListNavigationParams) => {
	const url = getMenuUrl(params.collectionId)
	ionHistory.push(url, {
		...params,
		prevPagePath: ionHistory.location.pathname,
	})
}

// Is referenced in mytours-read-common/src/collectionlist/containers/collectionlist.ts but the actual component does not use it
const navigateToTourDetails = (params: CollectionListNavigationParams) => {
	const url = getTourUrl(params.collectionId)
	ionHistory.push(url, {
		...params,
		prevPagePath: ionHistory.location.pathname,
	})
}

const navigateToScreenStory = (params: ScreenNavigationParams, parentItemId?: number) => {
	const url = getStoryUrl(params.itemId, params.accountId)
	ionHistory.push(url, {
		...params,
		parentItemId,
		prevPagePath: ionHistory.location.pathname,
	})
}

const navigateToScreenWeb = (params: ScreenNavigationParams, parentItemId?: number) => {
	const url = getWebUrl(params.itemId, params.accountId)
	ionHistory.push(url, {
		...params,
		parentItemId,
		prevPagePath: ionHistory.location.pathname,
	})
}

const navigateToScreenPanorama = (params: ScreenNavigationParams, parentItemId?: number) => {
	const url = getPanoramaUrl(params.itemId, params.accountId)
	ionHistory.push(url, {
		...params,
		parentItemId,
		prevPagePath: ionHistory.location.pathname,
	})
}

const pushModalWindow = (
	itemType: GetCollectionItemsIndexResponseData.ItemTypeEnum,
	itemSubtype: GetCollectionItemsIndexResponseData.ItemSubtypeEnum,
	itemId: number,
	itemVersion?: number,
	accountId?: number,
) => {
	const searchParams = new URLSearchParams(location.search)

	searchParams.append('m', `${itemType}_${itemSubtype};${itemId};${itemVersion};${accountId}`)

	const query = searchParams.toString()

	ionHistory.replace(location.pathname + (query ? '?' + query : '') + location.hash)
}

const popModalWindow = (itemSubtype: string, itemId: number) => {
	const searchParams = new URLSearchParams(location.search)
	const modalWindowsParams = searchParams.getAll('m')

	modalWindowsParams.forEach((param, index, object) => {
		const paramArray = param.split(';')

		if (itemSubtype === paramArray[0] && Number(itemId) === Number(paramArray[1])) {
			object.splice(index, 1)
		}
	})

	searchParams.delete('m')
	modalWindowsParams.forEach(param => searchParams.append('m', param))

	const query = searchParams.toString()

	ionHistory.replace(location.pathname + (query ? '?' + query : '') + location.hash)
}

interface NavigateToCollectionItems {
	params: CollectionItemsNavigationParams
	collectionType: CollectionItemsSlug
	itemType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum | 'item'
	stateToUpdate?: Record<string, string | number>
	queryParams?: Record<string, string | number>
	isOnlyChangePage?: boolean
}
const navigateToCollectionItems = ({
	params,
	collectionType,
	itemType,
	stateToUpdate = {},
	queryParams = {},
	isOnlyChangePage,
}: NavigateToCollectionItems): void => {
	let url = ''
	if (
		itemType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List ||
		itemType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour ||
		itemType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu
	) {
		switch (itemType || collectionType) {
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List:
				url = getListUrl(params.itemId, params.accountId)
				break
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu:
				url = getMenuUrl(params.itemId, params.accountId)
				break
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour:
				url = getTourUrl(params.itemId, params.accountId)
				break
		}
	} else {
		url = getCollectionItemsUrl(
			params.collectionId,
			params.itemId,
			collectionType,
			itemType,
			params.collectionAccountId,
			params.accountId,
		)
	}
	const historyParams: HistoryParamsType & CollectionItemsNavigationParams = {
		...params,
	}

	if (!isOnlyChangePage) {
		historyParams.prevPagePath = ionHistory.location.pathname
	} else if (ionHistory.location.state && (ionHistory.location.state as any).prevPagePath) {
		// TODO: Property 'prevPagePath' does not exist on type 'unknown'
		historyParams.prevPagePath = (ionHistory.location.state as any).prevPagePath
	}

	let query = ionHistory.location.search
	if (Object.keys(queryParams).length > 0) {
		const searchParams = new URLSearchParams(window.location.search)

		Object.entries(queryParams).forEach(([key, value]) => {
			if (searchParams.has(key)) {
				searchParams.delete(key)
			}

			searchParams.append(key, String(value))
		})

		query = searchParams.toString()
	}

	const queryString = query ? (query.startsWith('?') ? query : '?' + query) : ''

	ionHistory.push(url + queryString + ionHistory.location.hash, {
		...historyParams,
		...stateToUpdate,
	})
}

const navigateToItem = params => {
	const url = getListItemUrl(params.itemId, params.type, params.includeTabIndex, params.accountId)

	ionHistory.push(url, {
		...params,
		prevPagePath: ionHistory.location.pathname,
	})
}

export default {
	navigateToCollectionList,
	navigateToMenu,
	navigateToTourDetails,
	navigateToCollectionItems,
	navigateToScreenStory,
	navigateToScreenWeb,
	navigateToScreenPanorama,
	navigationStack,
	navigateToItem,
	pushModalWindow,
	popModalWindow,
}
